import React, { useState, useEffect, useMemo } from "react";
import {
  Mail, Phone, Building2, UserRound, Trash2, MailOpen, MailCheck, Loader2, Inbox,
} from "lucide-react";
import { adminGetMessages, adminToggleRead, adminDeleteMessage } from "../../api";

const MessagesPanel = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread

  const load = () => {
    setLoading(true);
    adminGetMessages()
      .then((data) => setMessages(Array.isArray(data) ? data : []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const toggleRead = async (id) => {
    const res = await adminToggleRead(id);
    setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, read: res.read } : m)));
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć tę wiadomość?")) return;
    await adminDeleteMessage(id);
    setMessages((ms) => ms.filter((m) => m.id !== id));
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  const shown = useMemo(
    () => (filter === "unread" ? messages.filter((m) => !m.read) : messages),
    [messages, filter]
  );

  const fmtDate = (d) => {
    try {
      return new Date(d).toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return d;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-white/50">
        <Loader2 className="h-5 w-5 animate-spin" /> Ładowanie wiadomości...
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-white">Wiadomości</h2>
          {unreadCount > 0 && (
            <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-bold text-black">
              {unreadCount} nowych
            </span>
          )}
        </div>
        <div className="inline-flex rounded-full border border-white/15 bg-white/5 p-1">
          {[
            { id: "all", label: "Wszystkie" },
            { id: "unread", label: "Nieprzeczytane" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
                filter === f.id ? "bg-white text-black" : "text-white/65 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 py-20 text-white/50">
          <Inbox className="h-8 w-8" />
          <p className="text-sm">Brak wiadomości do wyświetlenia.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {shown.map((m) => (
            <div
              key={m.id}
              className={`glass-card rounded-2xl p-6 ${!m.read ? "border-white/30" : ""}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 border border-white/20">
                    {m.role === "company" ? (
                      <Building2 className="h-4 w-4 text-white" />
                    ) : (
                      <UserRound className="h-4 w-4 text-white" />
                    )}
                  </span>
                  <div>
                    <p className="font-semibold text-white">
                      {m.name}{" "}
                      {!m.read && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-white align-middle" />}
                    </p>
                    <p className="text-xs text-white/50">
                      {m.role === "company" ? "Pracodawca" : "Pracownik"}
                      {m.company_name ? ` · ${m.company_name}` : ""} · {m.lang?.toUpperCase()}
                    </p>
                  </div>
                </div>
                <span className="text-xs text-white/40">{fmtDate(m.created_at)}</span>
              </div>

              {m.subject && (
                <p className="mt-4 text-sm font-medium text-white/90">Temat: {m.subject}</p>
              )}
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-white/70">{m.message}</p>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/55">
                <a href={`mailto:${m.email}`} className="flex items-center gap-1.5 hover:text-white">
                  <Mail className="h-3.5 w-3.5" /> {m.email}
                </a>
                {m.phone && (
                  <a href={`tel:${m.phone}`} className="flex items-center gap-1.5 hover:text-white">
                    <Phone className="h-3.5 w-3.5" /> {m.phone}
                  </a>
                )}
              </div>

              <div className="mt-5 flex gap-3 border-t border-white/10 pt-4">
                <button
                  onClick={() => toggleRead(m.id)}
                  className="glass-btn px-4 py-2 text-xs font-medium"
                >
                  {m.read ? <><MailOpen className="h-4 w-4" /> Oznacz jako nieprzeczytane</> : <><MailCheck className="h-4 w-4" /> Oznacz jako przeczytane</>}
                </button>
                <button
                  onClick={() => remove(m.id)}
                  className="glass-btn px-4 py-2 text-xs font-medium text-red-300 hover:text-red-200"
                >
                  <Trash2 className="h-4 w-4" /> Usuń
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MessagesPanel;
